/**
 * Admin Transactions Page
 *
 * Transaction explorer with type filters, search, stats, and pagination.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

// ==================== API ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface TxStats {
  totalTransactions: number;
  transactions24h: number;
  byType: { type: string; count: number }[];
  byType24h: { type: string; count: number }[];
  latestLedger: number;
  ledgerHealth: Record<string, number>;
  failedLedgersWithRetries: number;
}

interface Transaction {
  transaction_hash: string;
  transaction_type: string;
  account: string;
  destination: string | null;
  ledger_index: number;
  timestamp: string;
  nft_id: string | null;
  amount: string | null;
  offer_id: string | null;
}

// ==================== Helpers ====================

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function truncateHash(hash: string): string {
  return `${hash.slice(0, 8)}...${hash.slice(-6)}`;
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function formatXrp(drops: string | null): string {
  if (!drops) return '-';
  const num = Number(drops);
  if (num === 0) return '-';
  return `${(num / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} XRP`;
}

const TX_TYPE_COLORS: Record<string, string> = {
  NFTokenMint: 'bg-green-100 text-green-800',
  NFTokenCreateOffer: 'bg-blue-100 text-blue-800',
  NFTokenAcceptOffer: 'bg-purple-100 text-purple-800',
  NFTokenCancelOffer: 'bg-orange-100 text-orange-800',
  NFTokenBurn: 'bg-red-100 text-red-800',
};

const TX_TYPE_SHORT: Record<string, string> = {
  NFTokenMint: 'Mint',
  NFTokenCreateOffer: 'Create Offer',
  NFTokenAcceptOffer: 'Accept Offer',
  NFTokenCancelOffer: 'Cancel Offer',
  NFTokenBurn: 'Burn',
};

function TypeBadge({ type }: { type: string }) {
  const color = TX_TYPE_COLORS[type] || 'bg-gray-100 text-gray-800';
  const label = TX_TYPE_SHORT[type] || type;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

const TX_TYPES = ['All', 'NFTokenMint', 'NFTokenCreateOffer', 'NFTokenAcceptOffer', 'NFTokenCancelOffer', 'NFTokenBurn'];

// ==================== Component ====================

export function AdminTransactionsPage() {
  const [stats, setStats] = useState<TxStats | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState('All');
  const [accountFilter, setAccountFilter] = useState('');
  const [nftFilter, setNftFilter] = useState('');
  const [page, setPage] = useState(1);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/admin/transactions/stats');
      setStats(res.data);
    } catch (err: any) {
      console.error('Failed to load tx stats:', err);
    }
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('limit', '50');
      if (typeFilter !== 'All') params.append('type', typeFilter);
      if (accountFilter) params.append('account', accountFilter.trim());
      if (nftFilter) params.append('nft_id', nftFilter.trim());

      const res = await fetchWithAuth(`/admin/transactions?${params}`);
      setTransactions(res.data.transactions);
      setPagination(res.data.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter, accountFilter, nftFilter]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total Transactions" value={stats.totalTransactions.toLocaleString()} />
            <StatCard label="24h Volume" value={stats.transactions24h.toLocaleString()} />
            <StatCard
              label="Top Type (24h)"
              value={TX_TYPE_SHORT[stats.byType24h[0]?.type] || '-'}
              subtext={stats.byType24h[0] ? `${stats.byType24h[0].count} txns` : ''}
            />
            <StatCard label="Latest Ledger" value={stats.latestLedger.toLocaleString()} />
          </div>
        )}

        {error && <ErrorDisplay error={error} />}

        {/* Type filter buttons */}
        <div className="flex flex-wrap gap-2">
          {TX_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => { setTypeFilter(t); setPage(1); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-full ${
                typeFilter === t
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {TX_TYPE_SHORT[t] || t}
            </button>
          ))}
        </div>

        {/* Search filters */}
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Filter by account address..."
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadTransactions()}
            className="flex-1 max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <input
            type="text"
            placeholder="Filter by NFT ID..."
            value={nftFilter}
            onChange={(e) => setNftFilter(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadTransactions()}
            className="flex-1 max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={() => { setPage(1); loadTransactions(); }}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Search
          </button>
        </div>

        {/* Transactions table */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TX Hash</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ledger</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No transactions found</td></tr>
                ) : (
                  transactions.map((tx) => (
                    <tr key={tx.transaction_hash} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <a
                          href={`https://bithomp.com/explorer/${tx.transaction_hash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-mono"
                        >
                          {truncateHash(tx.transaction_hash)}
                        </a>
                      </td>
                      <td className="px-4 py-3"><TypeBadge type={tx.transaction_type} /></td>
                      <td className="px-4 py-3 text-sm font-mono text-gray-700">{truncateAddress(tx.account)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{tx.ledger_index?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatXrp(tx.amount)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(tx.timestamp)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
              <span className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} total)
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                  disabled={page === pagination.totalPages}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

// ==================== Stat Card ====================

function StatCard({ label, value, subtext }: {
  label: string;
  value: string | number;
  subtext?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
    </div>
  );
}
